// web/src/player/musicBed.ts — Music bed WebAudio player (Task 12)
//
// Port of BackgroundMusicManager.swift constants (targetVolume=0.25, fadeOut=1.5),
// with web-specific divergences:
//   targetVolume 0.25 → 0.15 — the web Gemini-TTS voice sits quieter against the bed
//     than iOS AVSpeech did; lowering the bed (not boosting the voice, which is near
//     full scale and would clip) restores the dialogue-forward balance. Tune here.
//   fadeOutDuration 1.5 → 2.5 — longer outro fade for a softer ending button.
//   numberOfLoops = -1 → AudioBufferSourceNode.loop = true
//
// Music is NOT dynamically ducked — the constant is the whole model.
// Node graph: AudioBufferSourceNode (loop=true) → GainNode → destination

const TARGET_VOLUME = 0.15;
const FADE_IN_DURATION = 0.5;   // seconds — mirrors fadeInDuration in Swift
const FADE_OUT_DURATION = 2.5;  // seconds — longer outro fade for a softer ending button (was 1.5)

export class MusicBed {
  private ctx: AudioContext;
  private destination: AudioNode;
  private buffer: AudioBuffer | null = null;
  private source: AudioBufferSourceNode | null = null;
  private gainNode: GainNode | null = null;
  /** True after stopWithFadeOut() or dispose() so double-stop is a no-op. */
  private stopped = false;

  constructor(ctx: AudioContext, destination: AudioNode) {
    this.ctx = ctx;
    this.destination = destination;
  }

  // ---------------------------------------------------------------------------
  // load
  // ---------------------------------------------------------------------------

  /**
   * Fetch the audio file at `url` and decode it into an AudioBuffer.
   * Must be called (and awaited) before `start()`.
   */
  async load(url: string): Promise<void> {
    const response = await fetch(url);
    const arrayBuffer = await response.arrayBuffer();
    this.buffer = await this.ctx.decodeAudioData(arrayBuffer);
  }

  // ---------------------------------------------------------------------------
  // start
  // ---------------------------------------------------------------------------

  /**
   * Create a looping AudioBufferSourceNode, connect through a GainNode to
   * `destination`, schedule a fade-in ramp 0 → 0.25 over 0.5 s, then start.
   *
   * @param whenOffsetSec Position within the track to start from (for
   *   resuming mid-playback). Defaults to 0 (start of track).
   */
  start(whenOffsetSec = 0): void {
    if (!this.buffer) {
      throw new Error('MusicBed: call load() before start()');
    }

    this.stopped = false;

    // ── Guard: stop and disconnect any prior source before creating a new one ─
    if (this.source) {
      try { this.source.stop(); } catch { /* already stopped — ignore */ }
      this.source.disconnect();
      this.source = null;
    }
    if (this.gainNode) {
      this.gainNode.disconnect();
      this.gainNode = null;
    }

    // ── Source node ──────────────────────────────────────────────────────────
    const source = this.ctx.createBufferSource();
    source.buffer = this.buffer;
    source.loop = true; // mirrors numberOfLoops = -1 in Swift

    // ── Gain node (starts silent) ─────────────────────────────────────────
    const gain = this.ctx.createGain();
    gain.gain.value = 0;

    // ── Wire up: source → gain → destination ─────────────────────────────
    source.connect(gain);
    gain.connect(this.destination);

    // ── Fade-in ramp: 0 → TARGET over 0.5 s. The setValueAtTime anchor pins the ramp's
    // start point to NOW — without it, linearRamp interpolates from the previous event
    // (or t=0), which on a mid-track (re)start makes the ramp effectively instant. ──
    const now = this.ctx.currentTime;
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(TARGET_VOLUME, now + FADE_IN_DURATION);

    // ── Start playback ────────────────────────────────────────────────────
    source.start(now, whenOffsetSec);

    this.source = source;
    this.gainNode = gain;
  }

  // ---------------------------------------------------------------------------
  // stopWithFadeOut
  // ---------------------------------------------------------------------------

  /**
   * Ramp gain from the CURRENT level to 0 over FADE_OUT_DURATION, then stop the source.
   * Safe to call multiple times — subsequent calls are no-ops.
   *
   * The cancel + setValueAtTime anchor is load-bearing: linearRampToValueAtTime ramps
   * from the LAST SCHEDULED EVENT — which here is the fade-in ramp at t≈0.5s. Without
   * the anchor, a fade scheduled a minute into playback interpolates that whole span,
   * so the value at "now" is already ≈0 and the music audibly CUTS instead of fading.
   */
  stopWithFadeOut(): void {
    if (this.stopped || !this.source || !this.gainNode) return;
    this.stopped = true;

    const now = this.ctx.currentTime;
    this.gainNode.gain.cancelScheduledValues(now);
    this.gainNode.gain.setValueAtTime(this.gainNode.gain.value, now);
    this.gainNode.gain.linearRampToValueAtTime(0, now + FADE_OUT_DURATION);
    this.source.stop(now + FADE_OUT_DURATION);
  }

  // ---------------------------------------------------------------------------
  // dispose
  // ---------------------------------------------------------------------------

  /**
   * Stop playback immediately and disconnect all nodes. Safe to call before
   * `start()` or after `stopWithFadeOut()`.
   */
  dispose(): void {
    if (this.source) {
      try {
        this.source.stop();
      } catch {
        // Already stopped — ignore the InvalidStateError
      }
      this.source.disconnect();
      this.source = null;
    }
    if (this.gainNode) {
      this.gainNode.disconnect();
      this.gainNode = null;
    }
    this.stopped = true;
  }
}
